import { createWebHistory, createRouter } from 'vue-router';
import PickLastDatePage from '@/views/pages/Calendar/CalendarPage.vue';
import PeriodInfo from '@/views/pages/Registration/PeriodInfoPage.vue';
import PersonalInfo from '@/views/pages/Registration/PersonalInfoPage.vue';
import Agreement from '@/views/pages/Registration/AgreementPage.vue';
import GiftPage from '@/views/pages/Account/GiftPage.vue';
import EditInfo from '@/views/pages/Account/EditInfoPage.vue';
import EditPeriod from '@/views/pages/Account/EditPeriodPage.vue';
import PageNotFound from '@/views/pages/404.vue';
import LoadingPage from '@/views/components/FullPageSpinner.vue';
import HowToPage from '@/views/pages/HowTo/HowToPage.vue';
import * as api from '@/services';
import store from '@/store';
import { resolveLineAuth } from '@/lib/dev-auth';
import { liffLog } from '@/lib/liff-log';

const routes = [
  {
    path: '/',
    name: 'LoadingPage',
    component: LoadingPage,
    meta: {
      title: 'NCare',
      isRegisPage: false,
      isEntryPage: true,
    },
  },
  {
    path: '/agreement',
    name: 'Agreement',
    component: Agreement,
    meta: {
      title: 'NCare - สมัครสมาชิก',
      isRegisPage: true,
      isEntryPage: false,
    },
  },
  {
    path: '/personal-info',
    name: 'PersonalInfo',
    component: PersonalInfo,
    meta: {
      title: 'NCare - สมัครสมาชิก',
      isRegisPage: true,
      isEntryPage: false,
    },
  },
  {
    path: '/period-info',
    name: 'PeriodInfo',
    component: PeriodInfo,
    meta: {
      title: 'NCare - สมัครสมาชิก',
      isRegisPage: true,
      isEntryPage: false,
    },
  },
  {
    path: '/pick-date',
    name: 'PickDate',
    component: PickLastDatePage,
    meta: {
      title: 'NCare - สมัครสมาชิก',
      isRegisPage: true,
      isEntryPage: false,
    },
  },
  {
    path: '/period-update',
    name: 'PeriodUpdate',
    component: PickLastDatePage,
    meta: {
      title: 'NCare - บันทึกประจำเดือน',
      isRegisPage: false,
      isEntryPage: false,
    },
  },
  {
    path: '/edit-info',
    name: 'EditInfo',
    component: EditInfo,
    meta: {
      title: 'NCare - แก้ไขข้อมูล',
      isRegisPage: false,
      isEntryPage: false,
    },
  },
  {
    path: '/edit-period-info',
    name: 'EditPeriodInfo',
    component: PeriodInfo,
    meta: {
      title: 'NCare - แก้ไขข้อมูล',
      isRegisPage: false,
      isEntryPage: false,
    },
  },
  {
    path: '/edit-period',
    name: 'EditPeriod',
    component: EditPeriod,
    meta: {
      title: 'NCare - บันทึกประจำเดือน',
      isRegisPage: false,
      isEntryPage: true,
    },
  },
  {
    path: '/edit-date',
    name: 'EditDate',
    component: PickLastDatePage,
    meta: {
      title: 'NCare - แก้ไขวันที่',
      isRegisPage: false,
      isEntryPage: false,
    },
  },
  // {
  //   path: '/pg',
  //   name: 'Premium Gift',
  //   component: GiftPage,
  //   meta: {
  //     title: 'NCare - รับของขวัญ',
  //     isRegisPage: false,
  //     isEntryPage: true,
  //   },
  // },
  {
    path: '/premium-gift',
    name: 'Premium Gift',
    component: GiftPage,
    meta: {
      title: 'NCare - รับของขวัญ',
      isRegisPage: false,
      isEntryPage: true,
    },
  },
  {
    path: '/how',
    name: 'HowPage',
    component: HowToPage,
    meta: {
      title: 'NCare - วิธีการใช้งาน',
      isRegisPage: false,
      isEntryPage: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'PageNotFound',
    component: PageNotFound,
    meta: {
      title: 'Page Not Found :(',
      isRegisPage: false,
      isEntryPage: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to, from, next) => {
  liffLog.debug('route guard', { from: from.fullPath, to: to.fullPath, name: to.name });

  if (!store.state.lineUid || to.meta.isEntryPage) {
    try {
      await resolveLineAuth(store);
    } catch (error) {
      liffLog.error('LINE auth failed', error);
      next(false);
      return;
    }
  }

  if (!store.state.lineUid) {
    liffLog.warn('No lineUid after auth — navigation stopped', liffLog.snapshot());
    next(false);
    return;
  }

  let data;
  try {
    const response = await api.getCustomerByLineUid(store.state.lineUid);
    data = response.data;
    liffLog.debug('getCustomerByLineUid ok', {
      lineUid: store.state.lineUid,
      resultCount: data.results?.length ?? 0,
    });
  } catch (error) {
    liffLog.error('getCustomerByLineUid failed', {
      lineUid: store.state.lineUid,
      error,
    });
    next(false);
    return;
  }

  // Guard against unexpected API response shapes (e.g. interceptor wrapping a
  // non-array early return from the controller).
  const results = Array.isArray(data?.results) ? data.results : [];

  // Verify the first item is actually a customer object with expected fields.
  const rawCustomer = results[0];
  const customer =
    rawCustomer && typeof rawCustomer.name !== 'undefined' ? rawCustomer : null;

  liffLog.debug('customer resolved', {
    resultCount: results.length,
    hasCustomer: !!customer,
  });

  if (customer) {
    await store.commit('SET_USERID', customer.id);
  }

  if (to.name === 'PageNotFound') {
    next();
    return;
  }

  const records = Array.isArray(customer?.records) ? customer.records : [];

  // Not registered yet
  if (!customer && to.name !== 'Agreement') {
    document.title = 'NCare - สมัครสมาชิก';
    next({ name: 'Agreement' });
  }
  // Missing name
  else if (customer && !customer.name && to.name !== 'PersonalInfo') {
    document.title = 'NCare - สมัครสมาชิก';
    next({ path: '/personal-info' });
  }
  // Missing period info
  else if (customer && customer.name && !customer.volume && to.name !== 'PeriodInfo') {
    document.title = 'NCare - สมัครสมาชิก';
    next({ path: '/period-info' });
  }
  // No records yet
  else if (customer && customer.name && customer.volume && records.length === 0 && to.name !== 'PickDate') {
    document.title = 'NCare - สมัครสมาชิก';
    next({ path: '/pick-date' });
  }
  // Fully registered but navigated to a registration page
  else if (customer && customer.name && customer.volume && records.length !== 0 && to.meta.isRegisPage) {
    next({ path: '/edit-info' });
  }
  // Loading page (entry point) — redirect to main page
  else if (to.name === 'LoadingPage') {
    document.title = 'NCare - แก้ไขข้อมูล';
    next({ path: '/edit-info' });
  }
  else {
    document.title = to.meta.title;
    next();
  }
});

export default router;
